import { WatchQueryFetchPolicy } from "@apollo/client";
import { CampaignMessageType, CAMPAIGN_MESSAGE_TYPE_OPTIONS } from "@regal-voice/shared-types/entities";

import { OperatorType, QueryParams } from "@regal-voice/ui";
import { withApolloQueryOptionsFromProps } from "HOC/withApolloQuery";
import { useOrchestrationPathing } from "Pages/orchestration/useOrchestrationPathing";
import { getCampaigns, getCampaignsWithPreview } from "Services/marketing-api/campaigns/queries";
import { Campaign, CampaignType } from "Types/Campaign";

import { ActionFormItems, ActionFormItemsProps } from "./ActionsFormItems";

function getLinkProps(
    basePath: string,
    callType: CampaignMessageType,
    id?: string
): { linkTo: string; linkText: string } {
    const campaignType =
        callType == CAMPAIGN_MESSAGE_TYPE_OPTIONS.sms ? CampaignType.AUTOMATED : CampaignType.AGENT_TASK;
    return {
        linkTo: id ? `${basePath}/${id}` : `${basePath}/new?type=${campaignType}&channel=${callType}`,
        linkText: id ? "Edit Campaign" : "Create Campaign",
    };
}

function getCustomSmsLinkProps(
    basePath: string,
    callType: CampaignMessageType,
    id?: string
): { linkTo: string; linkText: string } {
    return {
        linkTo: id ? `${basePath}/${id}` : `${basePath}/new?type=${CampaignType.AGENT_TASK}&channel=${callType}`,
        linkText: id ? "Edit Campaign" : "Create Campaign",
    };
}

// we arent currently using a pagable reqest for this display, but we may
// want to in the future so we have added this here for now. We need to do some
// reseach into how to make antd select displays infinite scroll
const defaultQueryParams: Partial<QueryParams> = {
    pageSize: 150,
    orderBy: [
        {
            key: "updatedAt",
            value: "DESC",
        },
    ],
};

const PHONE_CAMPAIGN_OPTIONS = [CampaignType.AGENT_TASK, CampaignType.TRIGGERED, CampaignType.BLAST];
const SMS_CAMPAIGN_OPTIONS = [CampaignType.AUTOMATED, CampaignType.TRIGGERED, CampaignType.BLAST];
// for the cancled display we only want the agent task campaigns
const CANCELED_SMS_OPTIONS = [CampaignType.AGENT_TASK];
const callQueryOptions = (callType: CampaignMessageType, isCanceledNode = false) => {
    const filterValue =
        callType === CAMPAIGN_MESSAGE_TYPE_OPTIONS.phone_call
            ? PHONE_CAMPAIGN_OPTIONS
            : isCanceledNode
              ? CANCELED_SMS_OPTIONS
              : SMS_CAMPAIGN_OPTIONS;
    return {
        variables: {
            queryParams: {
                ...defaultQueryParams,
                filterBy: [
                    { key: "messageType", operator: OperatorType.IS, value: callType },
                    {
                        key: "type",
                        operator: OperatorType.CONTAINS,
                        value: filterValue,
                    },
                ],
            },
        },
        fetchPolicy: "cache-and-network" as WatchQueryFetchPolicy,
    };
};

const CallFormItemsWithQuery = withApolloQueryOptionsFromProps<ActionFormItemsProps, Campaign[]>(
    ActionFormItems,
    getCampaigns
);

const SMSFormItemsWithQuery = withApolloQueryOptionsFromProps<ActionFormItemsProps, Campaign[]>(
    ActionFormItems,
    getCampaignsWithPreview
);

const customSmsTaskQueryOptions = (callType: CampaignMessageType) => ({
    variables: {
        queryParams: {
            ...defaultQueryParams,
            filterBy: [
                { key: "messageType", operator: OperatorType.IS, value: callType },
                {
                    key: "type",
                    operator: OperatorType.IS,
                    value: [CampaignType.AGENT_TASK],
                },
            ],
        },
    },
    fetchPolicy: "cache-and-network" as WatchQueryFetchPolicy,
});

const AgentSMSTaskWithData = withApolloQueryOptionsFromProps<ActionFormItemsProps, Campaign[]>(
    ActionFormItems,
    getCampaignsWithPreview
);

export type AgentSMSTaskProps = {
    formValues: { campaign?: Campaign };
};

export function AgentSMSTask(props: AgentSMSTaskProps): JSX.Element {
    const { campaignsBasePath } = useOrchestrationPathing();
    const options = customSmsTaskQueryOptions(CAMPAIGN_MESSAGE_TYPE_OPTIONS.sms);
    return (
        <AgentSMSTaskWithData
            {...props}
            callType={CAMPAIGN_MESSAGE_TYPE_OPTIONS.sms}
            options={options}
            getLinkProps={(callType, id) => getCustomSmsLinkProps(campaignsBasePath, callType, id)}
        />
    );
}

export type CallFormItemsProps = {
    callType: CampaignMessageType;
    showLabel?: boolean;
    showCampaignEditLink?: boolean;
    formValues?: { campaign?: Campaign };
    isCanceledNode?: boolean;
};

export function CallFormItems(props: CallFormItemsProps) {
    const { campaignsBasePath } = useOrchestrationPathing();
    const options = callQueryOptions(props.callType, props.isCanceledNode);
    return (
        <CallFormItemsWithQuery
            {...props}
            options={options}
            getLinkProps={(callType, id) => getLinkProps(campaignsBasePath, callType, id)}
        />
    );
}

export function SMSFormItems(props: CallFormItemsProps) {
    const { campaignsBasePath } = useOrchestrationPathing();
    const options = callQueryOptions(props.callType, props.isCanceledNode);
    return (
        <SMSFormItemsWithQuery
            {...props}
            options={options}
            getLinkProps={(callType, id) => getLinkProps(campaignsBasePath, callType, id)}
        />
    );
}
